/**
 * Origin C "ระฆังที่ไม่ยอมดัง": the full chain from the wake under the bell tower to the sect choice, as data
 * (docs/design/2026-10-13-origin-c-chain.md; beats from docs/design/C0_TOWNS_BCD.md §4.5 on the 96 m greybox town-c).
 * Run by systems/originFull.ts. Coordinates are tiles of town-c (greybox, data/layouts/townGrey.ts) or of the 256 m
 * plains (the last two steps, by the stream). `{m:…|f:…}` picks the hero's sex. The whisper has no speaker (LORE P10).
 */
import { FERRYMAN, LOG_SPOT, STARTER_HORSE, STARTER_LOG } from './originChains';
import type { FullChainDef, FullStep } from './originFull';
import type { QuestDef } from '../quests';

const Z = 'town-c';
const P = 'plains';
const q = (s: string): string => `og-c-${s}`;

/** Step ids, in order. */
export const OC = {
  bell: q('bell'),
  graves: q('graves'),
  pupil: q('pupil'),
  oar: q('oar'),
  curfew: q('curfew'),
  marsh: q('marsh'),
  toll: q('toll'),
  quay: q('quay'),
  escort: q('escort'),
  horse: q('horse'),
  trail: q('trail'),
  bamboo: q('bamboo'),
  envoys: q('envoys'),
} as const;

/** The four sects that recruit at ท่าโคมริมธาร on the stream's west bank (all are joinable schools). */
export const OC_SECTS = ['mist-peak', 'pine-ink', 'bell-stone', 'dim-moon'] as const;

const quest = (
  id: string,
  title: string,
  offer: string,
  remind: string,
  done: string,
  objectives: QuestDef['objectives'],
  cultivation: number,
  gold: number,
  giver?: QuestDef['giver'],
): QuestDef => ({ id, title, offer, remind, done, objectives, rewards: { cultivation, gold }, ...(giver ? { giver } : {}) });

const AAN = 'นักพรตน้อยอาอัน';
const ABBOT = 'นักพรตเฒ่าจื้อหยวน';
const BAI = 'ไป๋ซวง';
const WEN = 'เหวินเยี่ยน';
const JIN = 'แม่ครูพายจิน';
const CHEN = 'ลุงแจวเรือเฉิน';
const STABLE = 'น้าหยางคอกม้า';
/** The standing dead: spar-strength bodies that never strike first (the dead make way for the mark). */
const STILL = { monster: 'sparrer', sprite: 'jiangshi' } as const;

const steps: FullStep[] = [
  {
    zone: Z,
    startAt: [84.5, 12.5],
    guide: { npc: 'aan' },
    talkLines: {
      aan: '{m:ศิษย์พี่|f:ศิษย์พี่หญิง}ฟื้นแล้ว! เมื่อคืนแสงแดงฟาดลงมาตอนท่านดึงเชือก แล้วท่านก็ล้มไป · ท่านอาจารย์รออยู่ที่ลานอาราม',
      abbot: 'ระฆังไม่ดัง แต่หลุมศพริมน้ำเปิด ข้าไม่ชอบเรื่องที่มาคู่กันแบบนี้ ไปนับหลุมที่เปิดให้ข้า แล้วอย่าเข้าใกล้น้ำหลังตะวันตก',
    },
    quest: quest(
      OC.bell,
      'ระฆังที่ไม่ดัง',
      'คืนนั้นระฆังไม่ดัง แต่หลุมศพเปิด · เจ้าตื่นที่เชิงหอระฆังของอารามเสาหมอก เชือกระฆังยังอยู่ในมือ ' +
        'เจ้าดึงสุดแรงแล้ว ทำไมมันไม่ดัง · คลิกที่คนเพื่อดูกรอบเป้าหมาย แล้วกดคุย',
      `คุยกับ${AAN}ข้างหอระฆัง แล้วไปหา${ABBOT}ที่ลานอาราม`,
      'ท่านอาจารย์ชี้ไปทางตะวันตก แถวหลุมศพริมกำแพงเหนือ ดินยังชื้นอยู่',
      [
        { type: 'talk', target: 'aan', count: 1, label: `คุยกับ${AAN}` },
        { type: 'talk', target: 'abbot', count: 1, label: `รับคำสั่ง${ABBOT}` },
      ],
      100,
      10,
      { name: AAN, where: 'ที่เชิงหอระฆัง' },
    ),
    ordered: true,
  },
  {
    zone: Z,
    guide: { spot: 'grave1' },
    spawns: [{ ...STILL, at: [64.5, 15.5], name: 'ศพตื่นใหม่' }],
    quest: quest(
      OC.graves,
      'นับหลุมศพ',
      'หลุมศพแถวริมกำแพงเหนือเปิดอยู่ห้าหลุม เดินนับจากตะวันออกไปตะวันตก · มีศพหนึ่งยืนโยกอยู่กลางแถว ' +
        'มันไม่ขยับจนกว่าจะถูกแตะ แต่พอตื่นแล้วจะย่อตัวนานก่อนพุ่ง เห็นมันย่อเมื่อไรให้ตั้งรับ',
      'นับหลุมที่เปิดทั้งห้าตามแถว · ศพตื่นใหม่ย่อตัวก่อนพุ่ง ให้ตั้งรับ (F) แล้วตีจนมันล้ม',
      'นับครบห้าหลุม ศพตื่นใหม่ล้มลงไม่ลุกอีก · ทุกหลุมเปิดจากข้างใน ไม่มีรอยขุดจากข้างนอกเลย',
      [
        { type: 'route', target: 'graves', count: 5, label: 'นับหลุมที่เปิด' },
        { type: 'block', count: 1, label: 'ตั้งรับตอนศพพุ่ง' },
        { type: 'kill', target: 'sparrer', count: 1, label: 'ล้มศพตื่นใหม่' },
      ],
      200,
      20,
    ),
  },
  {
    zone: Z,
    guide: { npc: 'duel' },
    quest: quest(
      OC.pupil,
      'ศิษย์พี่จากยอดเขา',
      `${BAI}ศิษย์อาวุโสสำนักยอดหมอกมาถึงท่าก่อนใคร "ได้ยินว่าระฆังเงียบ ข้าเลยมาดูคนที่ดึงเชือก" · ` +
        `นางให้${WEN}ศิษย์ของนางลองมือกับเจ้าบนลาน · คลิกที่${WEN} แล้วคลิกขวาที่รูป เลือกท้าประลอง`,
      `ท้าประลอง${WEN}ที่ลานท่าน้ำ แพ้ก็ไม่เสียอะไร ท้าใหม่ได้`,
      `${BAI}พยักหน้า "เจ้าไม่ได้ยืนตรงที่ฝ่ามือจะลง ดี" · นางบอกว่าทูตสำนักอื่นกำลังมา ถ้าระฆังดังอีกครั้ง`,
      [{ type: 'duelWin', count: 1, label: `ชนะประลอง${WEN}` }],
      400,
      60,
      { name: BAI, where: 'ที่ลานท่าน้ำ' },
    ),
  },
  {
    zone: Z,
    guide: { npc: 'jin' },
    grantQinggong: 'water-skim',
    spawns: [{ monster: 'sparrer-yard', at: [70.5, 47.5], name: 'คนพายคู่ซ้อม', sprite: 'farmer' }],
    talkLines: {
      jin: 'คนตายมาจากน้ำ จะรับมันต้องมีของยาว · ก่อนอื่นเรียนก้าวแรกบนน้ำ ลงเท้าเบา ถอนเท้าก่อนน้ำจะรู้ตัว นี่คือวิ่งบนน้ำ · ' +
        'แล้วลองท่าทำลายกับคนพายข้าง ๆ ข้า เขาชอบตั้งรับ',
    },
    quest: quest(
      OC.oar,
      'พายของแม่ครู',
      `${JIN}อดีตแม่เรือแห่งโรงฝึกยุทธ์เรียกเจ้าจากลานข้างโรงฝึก "ศพที่ขึ้นจากน้ำไม่กลัวดาบ แต่กลัวคนที่ไม่ยอมถอย"`,
      `คุยกับ${JIN}ข้างโรงฝึกยุทธ์ แล้วใช้ท่าทำลายตัดการ์ดคนพายคู่ซ้อมหนึ่งครั้ง`,
      'ได้วิชาตัวเบา วิ่งบนน้ำ ขั้นแรก · คนพายคู่ซ้อมหัวเราะ "การ์ดข้าแตกมาสิบปีแล้ว เพิ่งมีคนทำได้ในท่าเดียว"',
      [
        { type: 'talk', target: 'jin', count: 1, label: `เรียนวิ่งบนน้ำจาก${JIN}` },
        { type: 'guardBreak', count: 1, label: 'ท่าทำลายตัดการ์ดคนพายคู่ซ้อม' },
      ],
      250,
      30,
      { name: JIN, where: 'ข้างโรงฝึกยุทธ์' },
    ),
    ordered: true,
  },
  {
    zone: Z,
    guide: { spot: 'reedGate' },
    quest: quest(
      OC.curfew,
      'โคมยามเคอร์ฟิว',
      'ตะวันตกดินแล้ว ยามท่าน้ำประกาศห้ามออกจากบ้าน แต่ศพยังขึ้นจากบึงตะวันตก · ' +
        'ยามถือโคมสองคนเฝ้าทางหลังโรงคลัง มันกระแอมก่อนหันเสมอ เดินตอนมันหันหลัง',
      'ผ่านแสงโคมไปถึงชายบึงตะวันตก · ยามกระแอมแล้วจะหัน ให้หยุดรอนอกแสง',
      'ถึงชายบึงแล้ว ต้นอ้อสูงกว่าหัว กลิ่นน้ำนิ่งลอยมา',
      [{ type: 'route', target: 'curfew', count: 3, label: 'ผ่านแสงโคมไปถึงชายบึง (ถูกเห็น = กลับไปเริ่มใหม่)' }],
      200,
      20,
    ),
  },
  {
    zone: Z,
    guide: { spot: 'clapper' },
    spawns: [
      { ...STILL, at: [18.5, 37.5], name: 'ศพยืนนิ่ง' },
      { ...STILL, at: [13.5, 40.5], name: 'ศพยืนนิ่ง' },
      { ...STILL, at: [18.5, 43.5], name: 'ศพยืนนิ่ง' },
      { ...STILL, at: [13.5, 45.5], name: 'ศพยืนนิ่ง' },
    ],
    quest: quest(
      OC.marsh,
      'ทางที่คนตายหลีก',
      'ในดงอ้อมีศพยืนอยู่สี่ร่าง พอเจ้าเดินเข้าไป พวกมันเอียงตัวหลบให้ เหมือนรู้จักรอยบนฝ่ามือเจ้า · ' +
        'ไม่ต้องสู้ เดินผ่านไปหาของที่ห่อผ้าดำซึ่งจมอยู่สุดบึง',
      'เดินผ่านศพยืนนิ่งไปหาห่อผ้าดำสุดบึงตะวันตก · พวกมันไม่ทำร้ายถ้าเจ้าไม่แตะ',
      'ในผ้าดำคือลูกตุ้มระฆังใหญ่ มีคนถอดมันออกไปก่อนคืนแสงแดง ระฆังถึงไม่ดัง',
      [{ type: 'visit', target: 'clapper', count: 1, label: 'หาห่อผ้าดำในบึง' }],
      200,
      20,
    ),
  },
  {
    zone: Z,
    guide: { spot: 'towerTop' },
    timer: { seconds: 50, failTo: [12.5, 46.5], failText: 'น้ำขึ้นท่วมทางแล้ว ศพลอยขวางหมด · ตั้งต้นใหม่ที่บึง' },
    spawns: [
      { monster: 'jiangshi', at: [40.5, 24.5], name: 'ศพหิวแสง' },
      { monster: 'jiangshi', at: [64.5, 22.5], name: 'ศพหิวแสง' },
    ],
    quest: quest(
      OC.toll,
      'เสียงกระซิบในหอระฆัง',
      'น้ำกำลังขึ้น พอน้ำเต็มท่า ศพจะขึ้นทั้งแม่น้ำ · แบกลูกตุ้มกลับไปแขวนบนหอระฆังให้ทัน ' +
        'ศพหิวแสงสองร่างเดินวนทางเหนือ มันช้า วิ่งอ้อมได้',
      'วิ่งไปยอดหอระฆังทางตะวันออกเฉียงเหนือให้ทันเวลา · ศพหิวแสงช้า วิ่งอ้อมได้',
      'ลูกตุ้มเข้าที่ ก่อนเจ้าจะตี มีเสียงกระซิบข้างหู "เจ้าจะไม่มีวันพ่ายแพ้" แล้วระฆังก็ดังขึ้นทับเสียงนั้น ดังไปทั้งท่า',
      [{ type: 'visit', target: 'towerTop', count: 1, label: 'แขวนลูกตุ้มบนหอระฆัง (มีเวลาจำกัด)' }],
      300,
      40,
    ),
  },
  {
    zone: Z,
    guide: { spot: 'quay' },
    spawns: [
      { monster: 'jiangshi', at: [86.5, 50.5], name: 'ศพขึ้นท่า' },
      { monster: 'jiangshi', at: [80.5, 57.5], name: 'ศพขึ้นท่า' },
      { monster: 'jiangshi', at: [88.5, 63.5], name: 'ศพขึ้นท่า' },
    ],
    quest: quest(
      OC.quay,
      'ศพขึ้นท่า',
      `เสียงระฆังปลุกศพที่จมอยู่ใต้ท่า สามร่างปีนขึ้นกำแพงริมน้ำทางตะวันออก ${CHEN}ตะโกนจากเรือ "มันกระโดดสองจังหวะแล้วข่วน ตั้งรับตอนมันลงพื้น!"`,
      'ศพขึ้นท่าอยู่ริมกำแพงตะวันออก · มันกระโดดแล้วข่วน ตั้งรับ (F) ตอนมันลงพื้น',
      `ศพร่างสุดท้ายจมกลับลงน้ำ ${CHEN}ปีนขึ้นท่า "ข้าเห็นเรือศพ มันพายทวนน้ำขึ้นเหนือ ไม่มีคนพาย"`,
      [
        { type: 'kill', target: 'jiangshi', count: 3, label: 'ศพขึ้นท่า' },
        { type: 'block', count: 2, label: 'ตั้งรับตอนศพข่วน' },
      ],
      450,
      60,
    ),
  },
  {
    zone: Z,
    guide: { npc: 'chen' },
    quest: quest(
      OC.escort,
      'พาลุงเฉินไปคอกม้า',
      `${CHEN}คนเดียวที่เห็นเรือศพ ต้องไปบอกน้าหยางที่คอกม้าท่าเหนือให้เตรียมม้าตามไป แต่ขาเขายังสั่น พาเขาเดินไป ระวังศพที่ยังเดินอยู่`,
      `เดินนำ${CHEN}ไปคอกม้าใกล้ประตูเหนือ อย่าทิ้งห่าง เขาจะหยุดรอ`,
      `"ถึงแล้ว ข้าไม่ขึ้นม้าหรอก ข้าเป็นคนเรือ" ${CHEN}นั่งลงข้างคอก`,
      [{ type: 'escort', target: 'chen', count: 1, label: `พา${CHEN}ถึงคอกม้า` }],
      350,
      40,
    ),
  },
  {
    zone: Z,
    guide: { npc: 'stable' },
    grantMount: { npc: 'stable', mount: STARTER_HORSE },
    talkLines: {
      stable: 'เรือศพไปทางเหนือหรือ ม้าตัวนี้วิ่งริมน้ำมาทั้งชีวิต ข้ายกให้เจ้า กดปุ่มขี่พาหนะเพื่อขึ้น กดอีกครั้งเพื่อลง ลองขี่ไปลานตะวันตกก่อน',
    },
    quest: quest(
      OC.horse,
      'ม้าตัวแรก',
      `${STABLE}จูงม้าออกมาตัวหนึ่ง "ทางเรือศพเลียบน้ำ ม้าไปได้เร็วกว่าเรือ" รับม้าแล้วลองขี่ให้คล่อง`,
      `คุยกับ${STABLE} แล้วขี่ม้าไปลานตะวันตก`,
      'ม้ารู้ใจเจ้าแล้ว ม้าตัวนี้เป็นของเจ้า',
      [
        { type: 'talk', target: 'stable', count: 1, label: `รับม้าจาก${STABLE}` },
        { type: 'visit', target: 'horseRide', count: 1, label: 'ขี่ม้าไปลานตะวันตก' },
      ],
      300,
      60,
      { name: STABLE, where: 'ที่คอกม้าท่าเหนือ' },
    ),
    ordered: true,
  },
  {
    zone: Z,
    guide: { spot: 'trail1' },
    timer: { seconds: 40, failTo: [34.5, 14.5], failText: 'โคมดับไปก่อนเจ้าถึง · กลับคอกม้าแล้วควบใหม่' },
    quest: quest(
      OC.trail,
      'ตามโคมทวนน้ำ',
      'โคมกระดาษสามดวงลอยกลับทางลม ชี้ทางที่เรือศพไป ควบม้าไปให้ถึงแต่ละดวงก่อนมันดับ แล้วออกช่องประตูเหนือ',
      'ขี่ม้าตามโคมสามดวงตามลำดับให้ทันเวลา (ต้องอยู่บนหลังม้า) ดวงสุดท้ายอยู่ที่ช่องประตูเหนือ',
      'ดวงสุดท้ายลอยออกประตูเหนือ เลียบถนนลงไปทางทุ่งราบ ไปทางลำธารกลางทุ่ง',
      [
        { type: 'visit', target: 'trail1', count: 1, label: 'โคมดวงที่หนึ่ง (มุมตะวันตกเฉียงเหนือ)' },
        { type: 'visit', target: 'trail2', count: 1, label: 'โคมดวงที่สอง (ริมกำแพงเหนือ)' },
        { type: 'visit', target: 'trail3', count: 1, label: 'โคมดวงที่สาม (ช่องประตูเหนือ)' },
      ],
      350,
      60,
    ),
    ordered: true,
  },
  {
    zone: P,
    guide: { npc: 'ferryman' },
    grantMount: { npc: 'ferryman', mount: STARTER_LOG },
    talkLines: {
      ferryman:
        'โคมผ้าดำหรือ มันลอยลงกลางลำธารเมื่อครู่ ไม้ไผ่ท่อนนี้ยกให้ เลือกไผ่ลำเดียวเป็นพาหนะ ยืนบนลำแล้วพายตามไป มันติดอยู่ที่ปากบึงบัว',
    },
    quest: quest(
      OC.bamboo,
      'ไผ่ลำเดียว',
      'โคมดวงสุดท้ายลงน้ำที่ลำธารกลางทุ่ง ลุงพายไผ่ริมน้ำมีไม้ไผ่ให้หนึ่งท่อน ยืนบนมัน พายตามโคมไปปากบึงบัว แล้วขึ้นฝั่งตะวันตก',
      'คุยกับลุงพายไผ่ ขึ้นไม้ไผ่ พายลงกลางน้ำ ตามโคมไปปากบึงบัว แล้วขึ้นฝั่งตะวันตก',
      'โคมติดอยู่ในกอบัว ผ้าดำผืนเดียวกับที่ห่อลูกตุ้มระฆัง · ฝั่งตะวันตกมีธงหลายสีมารออยู่',
      [
        { type: 'talk', target: 'ferryman', count: 1, label: 'รับไม้ไผ่จากลุงพายไผ่' },
        { type: 'visit', target: 'logMid', count: 1, label: 'ยืนพายไม้ไผ่ลงกลางลำธาร' },
        { type: 'visit', target: 'pondMouth', count: 1, label: 'พายตามโคมไปปากบึงบัว' },
        { type: 'visit', target: 'westBank', count: 1, label: 'ขึ้นฝั่งตะวันตก' },
      ],
      400,
      60,
      { name: FERRYMAN.name, where: 'ริมลำธารกลางทุ่ง' },
    ),
    ordered: true,
  },
  {
    zone: P,
    guide: { npc: 'baiP' },
    quest: quest(
      OC.envoys,
      'ทูตริมธาร',
      `${BAI}ยืนรอที่ท่าโคมริมธาร "ระฆังดังอีกครั้ง ทูตทุกสำนักได้ยิน ท่านอาจารย์ของเจ้าเขียนมาว่า เจ้าเป็นคนเดียวที่คนตายหลีกทางให้" · ` +
        'ผู้แทนสี่สำนักอยู่ที่นี่ คุยกับแต่ละคนแล้วเลือกทางของเจ้า หรือจะเดินคนเดียวก็ได้',
      `คุยกับผู้แทนสำนักที่ฝั่งตะวันตก แล้วเข้าร่วมสำนัก · หรือบอก${CHEN}ว่าจะเดินคนเดียว`,
      'ทางของเจ้าเริ่มแล้ว · เรือศพยังพายทวนน้ำทุกคืนเดือนดับ และยังไม่มีใครรู้ว่าใครถอดลูกตุ้มระฆัง',
      [{ type: 'sect', count: 1, label: 'เข้าร่วมสำนัก หรือเดินคนเดียว' }],
      500,
      100,
    ),
  },
];

export const ORIGIN_C: FullChainDef = {
  origin: 'c',
  steps,
  duelRival: { name: WEN },
  npcs: [
    {
      id: 'aan',
      zone: Z,
      name: AAN,
      title: 'ศิษย์อารามเสาหมอก',
      sprite: 'townKid',
      at: [86.5, 14.5],
      idle: ['{m:ศิษย์พี่|f:ศิษย์พี่หญิง}ดึงเชือกแรงจนมือพอง ข้าเห็นนะ', 'ข้าไม่กลัวศพหรอก… กลัวนิดเดียว'],
      after: { step: OC.bell, to: [80.5, 24.5] },
    },
    {
      id: 'abbot',
      zone: Z,
      name: ABBOT,
      title: 'เจ้าอารามเสาหมอก',
      sprite: 'taoist',
      at: [78.5, 22.5],
      idle: ['ระฆังที่ไม่ดังคือคำถาม เจ้าต้องหาคำตอบเอง', 'คนตายหลีกทางให้เจ้า จำไว้ว่ามันไม่ใช่เรื่องดีเสมอไป'],
    },
    { id: 'bai', zone: Z, from: OC.pupil, name: BAI, title: 'ศิษย์อาวุโส · ยอดหมอก', sprite: 'lilac', at: [52.5, 61.5], idle: ['อย่ายืนตรงที่ฝ่ามือจะลง'] },
    { id: 'wen', zone: Z, from: OC.pupil, name: WEN, title: 'ศิษย์ยอดหมอก · ท้าประลองได้', sprite: 'heroWudang', at: [48.5, 61.5], idle: ['อีกยกไหม'] },
    { id: 'jin', zone: Z, name: JIN, title: 'ครูโรงฝึกยุทธ์ · อดีตแม่เรือ', sprite: 'farmerAunt', at: [67.5, 47.5], idle: ['น้ำไม่สู้ใคร แต่ไม่เคยแพ้ใคร'] },
    {
      id: 'chen',
      zone: Z,
      from: OC.quay,
      name: CHEN,
      title: 'คนแจวเรือข้ามฟาก',
      sprite: 'fisherOld',
      at: [82.5, 56.5],
      idle: ['ข้าแจวเรือข้ามฟากมาสามสิบปี ไม่เคยเห็นน้ำไหลกลับทาง'],
    },
    { id: 'stable', zone: Z, name: STABLE, title: 'คนเลี้ยงม้าท่าเหนือ', sprite: 'farmer', at: [34.5, 12.5], idle: ['ม้าริมน้ำไม่กลัวหมอก'] },
    // The stream's west bank on the plains (ท่าโคมริมธาร): the four envoys and the boatman.
    { id: 'baiP', zone: P, from: OC.bamboo, name: BAI, title: 'ทูตยอดหมอก', sprite: 'lilac', at: [142.5, 148.5], school: 'mist-peak', idle: ['ยอดหมอกสอนให้ไม่อยู่ตรงที่แรงจะลง'] },
    { id: 'luo', zone: P, from: OC.bamboo, name: 'ผู้จดหลัวอิง', title: 'ทูตหอหมึกสน', sprite: 'scholar', at: [140.5, 151.5], school: 'pine-ink', idle: ['คืนที่ระฆังไม่ดัง ข้าจดไว้แล้ว ขาดแค่ชื่อคนถอดลูกตุ้ม'] },
    { id: 'gong', zone: P, from: OC.bamboo, name: 'ช่างหล่อระฆังกงเถียน', title: 'ทูตหินระฆัง', sprite: 'monk', at: [142.5, 154.5], school: 'bell-stone', idle: ['ระฆังที่ไม่ดังไม่ใช่ระฆังเสีย คือระฆังที่ถูกปิดปาก'] },
    { id: 'yun', zone: P, from: OC.bamboo, name: 'อวิ๋นเซียง', title: 'ทูตจันทร์มัว', sprite: 'blissLady', at: [145.5, 155.5], school: 'dim-moon', idle: ['คนตายที่หลีกทาง ยังอยากให้ใครสักคนฟังเขา'] },
    {
      id: 'chenP',
      zone: P,
      from: OC.bamboo,
      name: CHEN,
      title: 'คนแจวเรือ · ตามมาทางน้ำ',
      sprite: 'fisherOld',
      at: [139.5, 148.5],
      wanderer: true,
      idle: ['ข้าแจวเรือคนเดียวมาทั้งชีวิต น้ำไม่เคยถามว่าข้าอยู่สำนักไหน'],
    },
  ],
  spots: {
    grave1: { at: [74.5, 12.5], r: 1.5 },
    reedGate: { at: [24.5, 33.5], r: 2 },
    clapper: { at: [10.5, 47.5], r: 2 },
    towerTop: { at: [84.5, 10.5], r: 2.5 },
    quay: { at: [84.5, 57.5], r: 8 },
    horseRide: { at: [18.5, 22.5], r: 5, mount: STARTER_HORSE },
    trail1: { at: [10.5, 10.5], r: 3, mount: STARTER_HORSE },
    trail2: { at: [28.5, 6.5], r: 3, mount: STARTER_HORSE },
    trail3: { at: [48.5, 3.5], r: 4, mount: STARTER_HORSE },
    logMid: { ...LOG_SPOT, mount: STARTER_LOG },
    pondMouth: { at: [154.5, 148.5], r: 2.5, mount: STARTER_LOG, water: true },
    westBank: { at: [144.5, 151.5], r: 3 },
  },
  routes: {
    graves: {
      id: 'graves',
      points: [
        { at: [74.5, 12.5], r: 1.5 },
        { at: [69.5, 12.5], r: 1.5 },
        { at: [64.5, 12.5], r: 1.5 },
        { at: [59.5, 12.5], r: 1.5 },
        { at: [54.5, 12.5], r: 1.5 },
      ],
      resetTo: [76.5, 12.5],
      failText: '',
    },
    curfew: {
      id: 'curfew',
      points: [
        { at: [54.5, 33.5], r: 1.5 },
        { at: [40.5, 33.5], r: 1.2 },
        { at: [24.5, 33.5], r: 1.5 },
      ],
      watchers: [
        { at: [47.5, 31.5], range: 5, band: 3, period: 3, warn: 0.8, startFacing: 1 },
        { at: [32.5, 31.5], range: 5, band: 3, period: 3, warn: 0.8, startFacing: -1 },
      ],
      resetTo: [56.5, 33.5],
      failText: 'ยามเห็นเจ้าแล้ว! "ใครออกมาตอนเคอร์ฟิว!" · ถอยกลับไปตั้งต้นหลังโรงฝึกยุทธ์',
    },
  },
  escorts: {
    chen: {
      id: 'chen',
      npc: 'chen',
      path: [
        [82.5, 56.5],
        [74.5, 34.5],
        [56.5, 28.5],
        [44.5, 18.5],
        [36.5, 13.5],
      ],
      speed: 1.6,
      leadRange: 8,
      waves: [{ atPathIndex: 2, monster: 'jiangshi', n: 2 }],
      waitText: 'ช้าหน่อย… ขาคนเรือไม่ได้มีไว้เดินบก',
    },
  },
  lines: {
    wandererPick: 'เดินคนเดียวก็ได้ น้ำพาคนคนเดียวไปได้ไกลกว่าที่คิด แต่ถ้าเห็นเรือไม่มีคนพาย อย่าตามมันไปลำพัง',
    joined: 'ดี ทางนี้มีคนรอเจ้าแล้ว',
    chosenAlready: 'เจ้าเลือกทางแล้ว ขอให้ระฆังดังตามเจ้าไป',
    allDone: 'ไปเถอะ ยุทธภพกว้างกว่าท่าน้ำนัก',
  },
};
